import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RedactedValue from '../components/RedactedValue';
import { useAuth } from '../contexts/AuthContext';
import UserDropdown from '../components/UserDropdown/UserDropdown';
import jobService from '../services/jobService';
import courseService from '../services/courseService';
import employerService from '../services/employerService';
import PreciseApplicationPathShowcase from '../components/PreciseApplicationPathShowcase';
import { 
  BookOpenIcon, 
  BriefcaseIcon, 
  AcademicCapIcon, 
  TrophyIcon,
  ChartBarIcon,
  UsersIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  StarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'jobs' | 'courses' | 'talent'>('jobs');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [realtimeJobs, setRealtimeJobs] = useState<any[]>([]);
  const [selectedCourseCategory, setSelectedCourseCategory] = useState('All');
  const [featuredJobs, setFeaturedJobs] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [topEmployers, setTopEmployers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper function for course category matching
  const matchesCourseCategory = (course: any, targetCategory: string): boolean => {
    if (targetCategory === 'All') return true;
    
    const courseCategory = course.industryCategory || course.category || course.subject || '';
    const courseCategoryLower = courseCategory.toLowerCase();
    const selectedCategoryLower = targetCategory.toLowerCase();
    
    // Direct match
    if (courseCategoryLower === selectedCategoryLower) return true;
    
    // Special mappings for common variations
    if (selectedCategoryLower === 'technology' && 
        (courseCategoryLower.includes('tech') || 
         courseCategoryLower.includes('programming') || 
         courseCategoryLower.includes('software') ||
         courseCategoryLower.includes('development'))) return true;
    
    if (selectedCategoryLower === 'healthcare' && 
        (courseCategoryLower.includes('health') || 
         courseCategoryLower.includes('medical') || 
         courseCategoryLower.includes('medicine'))) return true;
    
    return false;
  };

  // Load real data from database
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load ALL jobs first, then shuffle for live feed
        const allJobsResponse = await jobService.getJobs({ limit: 1000 });
        const allJobs = allJobsResponse.jobs;
        
        // Get employer data to fetch logos
        const employersResponse = await employerService.getEmployers({ limit: 500 });
        const allEmployersData = employersResponse.employers;
        
        // Create employer map by both ID and company name for better matching
        const employerMap = allEmployersData.reduce((acc, emp) => {
          acc[emp.id] = emp;
          // Also map by company name for fallback matching
          if (emp.companyName) {
            acc[emp.companyName.toLowerCase().trim()] = emp;
          }
          return acc;
        }, {} as Record<string, any>);
        
        // Shuffle jobs and take 20 for scrolling feed
        const shuffledJobs = [...allJobs].sort(() => Math.random() - 0.5);
        const formattedJobs = shuffledJobs.slice(0, 20).map(job => {
          // Try to match employer by ID first, then by company name
          let employer = employerMap[job.employerId];
          if (!employer && job.employerName) {
            employer = employerMap[job.employerName.toLowerCase().trim()];
          }
          
          const companyName = job.employerName || 'Company';
          
          return {
            id: job.id,
            title: stripHtml(job.title || ''),
            salary: job.salaryRange ? 
              `$${job.salaryRange.min.toLocaleString()} - $${job.salaryRange.max.toLocaleString()}` : 
              'Salary not specified',
            applicants: job.applicationsCount,
            company: companyName,
            logo: employer?.logo || null,
            location: stripHtml(job.location || 'Remote'),
            postedTime: getTimeAgo(job.createdAt),
            urgent: job.competitionLevel === 'high' || job.applicationsCount > 20
          };
        });
        setRealtimeJobs(formattedJobs);

        // Load 6 random featured jobs from all jobs with employer logos
        const randomFeaturedJobs = [...allJobs].sort(() => Math.random() - 0.5).slice(0, 6).map(job => {
          // Try to match employer by ID first, then by company name
          let employer = employerMap[job.employerId];
          if (!employer && job.employerName) {
            employer = employerMap[job.employerName.toLowerCase().trim()];
          }
          return {
            ...job,
            logo: employer?.logo || null
          };
        });
        setFeaturedJobs(randomFeaturedJobs);

        // Load courses - try multiple approaches (load more for filtering)
        try {
          const coursesData = await courseService.getPopularCourses(24); // Load more courses for filtering
          setCourses(coursesData);
        } catch (courseError) {
          console.log('Popular courses failed, trying all courses');
          try {
            const allCoursesResponse = await courseService.getCourses({ limit: 24 });
            setCourses(allCoursesResponse.courses || []);
          } catch (allCoursesError) {
            console.error('All courses failed:', allCoursesError);
            setCourses([]);
          }
        }

        // Build top employers directly from jobs data
        try {
          // Group jobs by employer
          const employerJobMap = new Map();
          
          allJobs.forEach(job => {
            if (!job.employerName || job.employerName.trim().length < 2) return;
            
            const employerKey = job.employerName.toLowerCase().trim();
            if (!employerJobMap.has(employerKey)) {
              employerJobMap.set(employerKey, {
                jobs: [],
                employerId: job.employerId,
                employerName: job.employerName,
                // Try to find employer data if available
                employerData: allEmployersData.find(emp => 
                  emp.id === job.employerId || 
                  (emp.companyName && emp.companyName.toLowerCase().trim() === employerKey)
                )
              });
            }
            employerJobMap.get(employerKey).jobs.push(job);
          });

          // Convert to top employers format
          const topEmployersList = Array.from(employerJobMap.entries())
            .map(([key, data]) => {
              const employer = data.employerData;
              return {
                id: employer?.id || data.employerId || `generated-${key}`,
                companyName: data.employerName,
                companyDescription: employer?.companyDescription || `${data.employerName} is actively hiring talent`,
                industry: employer?.industry || 'Technology',
                companySize: employer?.companySize || 'Unknown',
                location: employer?.location || data.jobs[0]?.location || 'Remote',
                website: employer?.website || '',
                logo: employer?.logo || null,
                employeeCount: employer?.employeeCount || 'Unknown',
                isVerified: employer?.isVerified || false,
                jobsPosted: data.jobs.length,
                activeJobs: data.jobs.length,
                averageRating: employer?.averageRating || 4.0,
                totalReviews: employer?.totalReviews || 0,
                createdAt: employer?.createdAt || new Date(),
                updatedAt: employer?.updatedAt || new Date(),
                jobs: data.jobs,
                recentJobs: data.jobs.slice(0, 5)
              };
            })
            .sort((a, b) => b.activeJobs - a.activeJobs) // Sort by most jobs
            .slice(0, 6); // Take top 6

          setTopEmployers(topEmployersList);
        } catch (employerError) {
          console.error('Error building top employers:', employerError);
          setTopEmployers([]);
        }

      } catch (error) {
        console.error('Error loading landing page data:', error);
        
        // Fallback to some basic data if API fails - create enough for scrolling
        const fallbackJobs = Array.from({ length: 15 }, (_, i) => ({
          id: `fallback-${i + 1}`,
          title: [
            'Software Developer', 'Data Analyst', 'UX Designer', 'Product Manager', 
            'DevOps Engineer', 'Frontend Developer', 'Backend Developer', 'Mobile Developer',
            'QA Engineer', 'Marketing Specialist', 'Sales Representative', 'Business Analyst',
            'Project Manager', 'Graphic Designer', 'Content Writer'
          ][i] || 'Software Developer',
          salary: 'Competitive salary',
          applicants: Math.floor(Math.random() * 50),
          company: [
            'Tech Company', 'Innovation Labs', 'Digital Solutions', 'Creative Agency',
            'Data Corp', 'Design Studio', 'StartupCo', 'Enterprise Ltd'
          ][i % 8],
          logo: null,
          location: ['Remote', 'San Francisco, CA', 'New York, NY', 'Austin, TX', 'Seattle, WA'][i % 5],
          postedTime: ['Just posted', '5 min ago', '15 min ago', '30 min ago', '1 hour ago'][i % 5],
          urgent: i % 4 === 0
        }));
        setRealtimeJobs(fallbackJobs);
        
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Set up periodic shuffle for live jobs (every 15 seconds for more activity)
    const interval = setInterval(() => {
      setRealtimeJobs(prevJobs => {
        if (prevJobs.length === 0) return prevJobs;
        // Shuffle existing jobs to show movement
        const shuffled = [...prevJobs].sort(() => Math.random() - 0.5);
        return shuffled;
      });
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  // Helper function to calculate time ago
  const getTimeAgo = (date: Date | string | any): string => {
    try {
      const now = new Date();
      let jobDate: Date;
      
      // Handle different date formats
      if (date instanceof Date) {
        jobDate = date;
      } else if (typeof date === 'string') {
        jobDate = new Date(date);
      } else if (date && date.seconds) {
        // Firestore timestamp
        jobDate = new Date(date.seconds * 1000);
      } else {
        return 'Recently posted';
      }
      
      // Check if date is valid
      if (isNaN(jobDate.getTime())) {
        return 'Recently posted';
      }
      
      const diffMs = now.getTime() - jobDate.getTime();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMinutes < 1) {
        return 'Just posted';
      } else if (diffMinutes < 60) {
        return `${diffMinutes} min ago`;
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      } else {
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      }
    } catch (error) {
      return 'Recently posted';
    }
  };

  // Helper function to strip HTML and clean text
  const stripHtml = (html: string): string => {
    if (!html) return '';
    // Remove HTML tags but keep the text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    let text = tempDiv.textContent || tempDiv.innerText || '';
    // Clean up extra whitespace and limit length
    text = text.replace(/\s+/g, ' ').trim();
    return text.length > 100 ? text.substring(0, 100) + '...' : text;
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    
    const routes = {
      jobs: '/jobs',
      courses: '/courses',
      talent: '/talent'
    };
    
    navigate(`${routes[searchType]}?search=${encodeURIComponent(searchQuery)}`);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 lg:py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-primary-600">Workazon</h1>
              </div>
            </div>

            {/* Search Bar */}
            <div className="hidden md:flex flex-1 max-w-2xl mx-4 lg:mx-8">
              <div className="relative w-full">
                <div className="flex w-full shadow-sm">
                  {/* Search Type Dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowSearchDropdown(!showSearchDropdown)}
                      className="flex items-center justify-center h-12 px-3 lg:px-4 bg-white border border-gray-300 rounded-l-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[100px] lg:min-w-[120px]"
                    >
                      {searchType === 'jobs' && <BriefcaseIcon className="h-4 w-4 mr-1 lg:mr-2" />}
                      {searchType === 'courses' && <BookOpenIcon className="h-4 w-4 mr-1 lg:mr-2" />}
                      {searchType === 'talent' && <UsersIcon className="h-4 w-4 mr-1 lg:mr-2" />}
                      <span className="hidden sm:inline">{searchType.charAt(0).toUpperCase() + searchType.slice(1)}</span>
                      <span className="sm:hidden">{searchType.charAt(0).toUpperCase()}</span>
                      <ChevronDownIcon className="h-4 w-4 ml-1 lg:ml-2" />
                    </button>
                    
                    {showSearchDropdown && (
                      <div className="absolute top-full left-0 mt-1 w-40 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                        <button
                          onClick={() => { setSearchType('jobs'); setShowSearchDropdown(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg"
                        >
                          <BriefcaseIcon className="h-4 w-4 mr-2" />
                          Jobs
                        </button>
                        <button
                          onClick={() => { setSearchType('courses'); setShowSearchDropdown(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <BookOpenIcon className="h-4 w-4 mr-2" />
                          Courses
                        </button>
                        {/* <button
                          onClick={() => { setSearchType('talent'); setShowSearchDropdown(false); }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg"
                        >
                          <UsersIcon className="h-4 w-4 mr-2" />
                          Talent
                        </button> */}
                      </div>
                    )}
                  </div>
                  
                  {/* Search Input */}
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder={`Search ${searchType}...`}
                    className="flex-1 h-12 px-4 border-t border-b border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                  
                  {/* Search Button */}
                  <button
                    onClick={handleSearch}
                    className="h-12 px-4 lg:px-6 bg-primary-600 text-white rounded-r-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Search Button */}
            <div className="md:hidden relative">
              <button
                onClick={() => setShowSearchDropdown(!showSearchDropdown)}
                className="p-2 text-gray-600 hover:text-gray-900"
              >
                <MagnifyingGlassIcon className="h-6 w-6" />
              </button>
              
              {/* Mobile Search Dropdown */}
              {showSearchDropdown && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                  <div className="p-2">
                    <p className="text-xs text-gray-500 mb-2">Search for:</p>
                    <button
                      onClick={() => { 
                        setSearchType('jobs'); 
                        setShowSearchDropdown(false); 
                        navigate('/jobs');
                      }}
                      className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md mb-1"
                    >
                      <BriefcaseIcon className="h-4 w-4 mr-2" />
                      Jobs
                    </button>
                    <button
                      onClick={() => { 
                        setSearchType('courses'); 
                        setShowSearchDropdown(false); 
                        navigate('/courses');
                      }}
                      className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md mb-1"
                    >
                      <BookOpenIcon className="h-4 w-4 mr-2" />
                      Courses
                    </button>
                    {/* <button
                      onClick={() => { 
                        setSearchType('talent'); 
                        setShowSearchDropdown(false); 
                        navigate('/talent');
                      }}
                      className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <UsersIcon className="h-4 w-4 mr-2" />
                      Talent
                    </button> */}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2 lg:space-x-4">
             {/*} <Link
                to="/rankings"
                className="text-secondary-600 hover:text-secondary-900 px-2 lg:px-3 py-2 rounded-md text-sm font-medium flex items-center"
              >
                <TrophyIcon className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Rankings</span>
              </Link> */}
              
              {currentUser ? (
                <UserDropdown className="flex items-center space-x-2" />
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-secondary-600 hover:text-secondary-900 px-2 lg:px-3 py-2 rounded-md text-sm font-medium"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="btn-primary text-sm px-3 lg:px-4 py-2"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          {/* Left side - Hero text and buttons */}
          <div className="w-full lg:w-1/2 text-left">
            <h2 className="text-7xl font-extrabold text-secondary-900 sm:text-5xl md:text-7xl">
              The
              <span className="text-primary-600"> Skills to Career</span> Pipeline
            </h2>
            <p className="mt-6 text-xl text-secondary-600">
              Learn skills, earn certifications, get validated by AI, and connect directly with employers. 
              Workazon transforms education into employment opportunities.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/register?type=student"
                className="btn-primary px-8 py-3 text-lg flex items-center justify-center"
              >
                Start Learning
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/register?type=employer"
                className="btn-outline px-8 py-3 text-lg"
              >
                Find Talent
              </Link>
            </div>
          </div>

          {/* Right side - Real-time jobs feed */}
          <div className="w-full lg:w-1/2 max-w-md mx-auto lg:mx-0 lg:mt-0">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    Live Job Feed
                  </h3>
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="w-1 h-1 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                    Live
                  </div>
                </div>
              </div>
              
              {/* Scrolling Job Feed */}
              <div className="relative h-80 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : (
                  <div className="space-y-2 p-4">
                    {realtimeJobs.length > 0 ? [...realtimeJobs, ...realtimeJobs].map((job, index) => (
                    <div 
                      key={`${job.id}-${index}`} 
                      className={`border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-all duration-300 hover:border-primary-300 cursor-pointer ${
                        job.urgent ? 'ring-1 ring-orange-200 bg-orange-50' : ''
                      }`}
                      onClick={() => navigate(`/jobs/${job.id}`)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                            {job.logo ? (
                              <img 
                                src={job.logo} 
                                alt={job.company} 
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  target.parentElement!.innerHTML = `
                                    <div class="w-full h-full bg-primary-100 text-primary-600 text-xs font-bold rounded-full flex items-center justify-center">
                                      ${job.company.charAt(0).toUpperCase()}
                                    </div>
                                  `;
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-primary-100 text-primary-600 text-xs font-bold rounded-full flex items-center justify-center">
                                {job.company.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="font-medium text-gray-900 text-sm leading-tight flex-1 pr-2 break-words overflow-hidden"
                                  style={{ 
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-word'
                                  }}>
                                {job.title}
                              </h4>
                              {job.urgent && (
                                <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded flex-shrink-0 ml-2">
                                  Urgent
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mb-1">{job.company}</p>
                            <p className="text-xs text-gray-500 mb-2">{job.location}</p>
                            <div className="flex items-center space-x-3">
                              <span className="flex items-center text-green-600 text-xs font-medium">
                                <CurrencyDollarIcon className="h-3 w-3 mr-1" />
                                {job.salary}
                              </span>
                              <span className="flex items-center text-blue-600 text-xs">
                                <UsersIcon className="h-3 w-3 mr-1" />
                                {job.applicants} applicants
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-2 ml-2">
                          <div className="text-xs text-gray-500">
                            {job.postedTime}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/jobs/${job.id}`);
                            }}
                            className="text-primary-600 hover:text-primary-700 text-xs font-medium flex items-center hover:bg-primary-50 px-2 py-1 rounded transition-colors"
                          >
                            View
                            <ArrowRightIcon className="h-3 w-3 ml-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No jobs available at the moment</p>
                      <p className="text-sm">Check back later for new opportunities</p>
                    </div>
                  )}
                  </div>
                )}
                
                {/* Gradient overlays for smooth scroll effect */}
                {!loading && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                  </>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Newest job listings
                  </div>
                  <Link
                    to="/jobs"
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
                  >
                    Browse all jobs
                    <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Precise Application Path — premium AI feature showcase */}
      <PreciseApplicationPathShowcase />

      {/* Features Section */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Background Marketing Images - Hidden on mobile to prevent overlap */}
        <div className="absolute inset-0 pointer-events-none hidden lg:block">
          {/* Student Image - 16:9 aspect ratio (widescreen) */}
          <div className="absolute top-20 left-10 w-64 h-36 lg:w-80 lg:h-48 xl:w-96 xl:h-54 rounded-2xl overflow-hidden opacity-30 lg:opacity-30 transform shadow-lg">
            <img src="/images/marketing/student.jpg" alt="" className="w-full h-full object-cover" />
          </div>
          {/* Training Image - 9:16 aspect ratio (portrait) */}
          <div className="absolute top-2 right-20 w-48 h-80 lg:w-60 lg:h-96 xl:w-72 xl:h-112 rounded-2xl overflow-hidden opacity-60 lg:opacity-80 transform shadow-lg">
            <img src="/images/marketing/training.jpg" alt="" className="w-full h-full object-cover" />
          </div>
          {/* HR Image - 1:1 aspect ratio (square) */}
          <div className="absolute bottom-40 right-80 transform -translate-x-1/2 w-32 h-32 lg:w-48 lg:h-48 xl:w-56 xl:h-56 rounded-2xl overflow-hidden opacity-50 lg:opacity-70 transform shadow-lg">
            <img src="/images/marketing/hr.jpg" alt="" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Simplified background for mobile/tablet */}
        <div className="absolute inset-0 pointer-events-none lg:hidden">
          <div className="absolute top-10 right-4 w-48 h-36 rounded-xl overflow-hidden opacity-20 transform shadow-md">
            <img src="/images/marketing/student.jpg" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute bottom-2 left-12 w-96 h-96 rounded-xl overflow-hidden opacity-60 transform shadow-md">
            <img src="/images/marketing/hr.jpg" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute top-96 right-1/2 w-48 h-96 rounded-xl overflow-hidden opacity-50 transform shadow-md">
            <img src="/images/marketing/training.jpg" alt="" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h3 className="text-3xl font-extrabold text-secondary-900">
              Everything You Need in One Platform
            </h3>
            <p className="mt-4 text-lg text-secondary-600">
              Comprehensive tools for learning, validation, and employment matching
            </p>
          </div>

          

          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {/* Feature 1 */}
            <div className="card hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ease-in-out transform hover:scale-105 bg-white/90 backdrop-blur-sm">
              <div className="card-body text-center">
                <div className="mx-auto h-12 w-12 text-primary-600 transition-transform duration-300 group-hover:scale-110">
                  <BookOpenIcon className="h-full w-full" />
                </div>
                <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                  Interactive Courses
                </h4>
                <p className="mt-2 text-secondary-600">
                  Access comprehensive courses with videos, quizzes, and hands-on projects. 
                  Learn at your own pace with expert-crafted content.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="card hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ease-in-out transform hover:scale-105 bg-white/90 backdrop-blur-sm">
              <div className="card-body text-center">
                <div className="mx-auto h-12 w-12 text-primary-600 transition-transform duration-300 group-hover:scale-110">
                  <AcademicCapIcon className="h-full w-full" />
                </div>
                <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                  AI-Validated Certifications
                </h4>
                <p className="mt-2 text-secondary-600">
                  Earn certifications validated by advanced AI algorithms that ensure 
                  reliability and prevent cheating.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="card hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ease-in-out transform hover:scale-105 bg-white/90 backdrop-blur-sm">
              <div className="card-body text-center">
                <div className="mx-auto h-12 w-12 text-primary-600 transition-transform duration-300 group-hover:scale-110">
                  <TrophyIcon className="h-full w-full" />
                </div>
                <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                  Global Rankings
                </h4>
                <p className="mt-2 text-secondary-600">
                  Compete globally and showcase your skills with public rankings 
                  that employers trust and value.
                </p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="card hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ease-in-out transform hover:scale-105 bg-white/90 backdrop-blur-sm">
              <div className="card-body text-center">
                <div className="mx-auto h-12 w-12 text-primary-600 transition-transform duration-300 group-hover:scale-110">
                  <BriefcaseIcon className="h-full w-full" />
                </div>
                <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                  Real-time Job Board
                </h4>
                <p className="mt-2 text-secondary-600">
                  Get matched with job opportunities in real-time based on your 
                  skills, certifications, and performance.
                </p>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="card hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ease-in-out transform hover:scale-105 bg-white/90 backdrop-blur-sm">
              <div className="card-body text-center">
                <div className="mx-auto h-12 w-12 text-primary-600 transition-transform duration-300 group-hover:scale-110">
                  <ChartBarIcon className="h-full w-full" />
                </div>
                <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                  Performance Analytics
                </h4>
                <p className="mt-2 text-secondary-600">
                  Track your progress with detailed analytics and insights that 
                  help you improve and grow professionally.
                </p>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="card hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ease-in-out transform hover:scale-105 bg-white/90 backdrop-blur-sm">
              <div className="card-body text-center">
                <div className="mx-auto h-12 w-12 text-primary-600 transition-transform duration-300 group-hover:scale-110">
                  <UsersIcon className="h-full w-full" />
                </div>
                <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                  Direct Employer Connection
                </h4>
                <p className="mt-2 text-secondary-600">
                  Connect directly with employers who are looking for your specific 
                  skills and qualifications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Jobs Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-extrabold text-secondary-900">
              Featured Job Opportunities
            </h3>
            <p className="mt-4 text-lg text-secondary-600">
              Apply directly with your skills or qualify by taking courses - showcase your evaluation performance to employers
            </p>
            {!currentUser && (
              <p className="text-sm text-gray-500 mt-3">
                <span className="inline-flex items-center">
                  🔒 Sign in to see personalized skill matches and application insights
                </span>
              </p>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="animate-pulse">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-8 h-8 bg-gray-300 rounded"></div>
                      <div>
                        <div className="h-4 bg-gray-300 rounded w-32 mb-2"></div>
                        <div className="h-3 bg-gray-300 rounded w-24"></div>
                      </div>
                    </div>
                    <div className="h-4 bg-gray-300 rounded mb-2"></div>
                    <div className="h-3 bg-gray-300 rounded w-3/4 mb-4"></div>
                    <div className="flex space-x-2">
                      <div className="h-8 bg-gray-300 rounded flex-1"></div>
                      <div className="h-8 bg-gray-300 rounded flex-1"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(featuredJobs.length > 0 ? featuredJobs : [
              {
                id: 1,
                title: 'Frontend Developer',
                employerName: 'TechStartup Inc.',
                location: 'San Francisco, CA',
                salaryRange: { min: 85000, max: 120000 },
                applicationsCount: 23,
                skillsRequired: ['React', 'TypeScript', 'CSS', 'JavaScript'],
                requiredCourses: ['Advanced React Development', 'TypeScript Fundamentals'],
                logo: '💻',
                autoOffer: {
                  enabled: true,
                  timeLimit: 48,
                  requiredCourses: [
                    { courseId: '1', courseName: 'Advanced React Development', minimumGrade: 85 },
                    { courseId: '2', courseName: 'TypeScript Fundamentals', minimumGrade: 80 }
                  ],
                  minimumOverallScore: 85
                },
                autoOfferDeadline: new Date(Date.now() + 48 * 60 * 60 * 1000)
              },
              {
                id: 2,
                title: 'Data Analyst',
                employerName: 'DataCorp',
                location: 'Remote',
                salaryRange: { min: 70000, max: 95000 },
                applicationsCount: 15,
                skillsRequired: ['Python', 'SQL', 'Tableau'],
                requiredCourses: ['Data Analysis with Python', 'SQL Mastery'],
                logo: '📊'
              },
              {
                id: 3,
                title: 'UX Designer',
                employerName: 'Design Studio',
                location: 'New York, NY',
                salaryRange: { min: 75000, max: 105000 },
                applicationsCount: 31,
                skillsRequired: ['Figma', 'User Research', 'Prototyping'],
                requiredCourses: ['UX Design Fundamentals', 'Advanced Prototyping'],
                logo: '🎨',
                autoOffer: {
                  enabled: true,
                  timeLimit: 72,
                  requiredCourses: [
                    { courseId: '3', courseName: 'UX Design Fundamentals', minimumGrade: 80 },
                    { courseId: '4', courseName: 'Advanced Prototyping', minimumGrade: 75 }
                  ],
                  minimumOverallScore: 80
                },
                autoOfferDeadline: new Date(Date.now() + 72 * 60 * 60 * 1000)
              },
              {
                id: 4,
                title: 'Backend Engineer',
                employerName: 'CloudTech',
                location: 'Austin, TX',
                salaryRange: { min: 90000, max: 130000 },
                applicationsCount: 18,
                skillsRequired: ['Node.js', 'AWS', 'MongoDB'],
                requiredCourses: ['Backend Development', 'Cloud Architecture'],
                logo: '⚙️'
              },
              {
                id: 5,
                title: 'Mobile Developer',
                employerName: 'AppWorks',
                location: 'Seattle, WA',
                salaryRange: { min: 80000, max: 115000 },
                applicationsCount: 27,
                skillsRequired: ['React Native', 'iOS', 'Android'],
                requiredCourses: ['Mobile App Development', 'React Native Mastery'],
                logo: '📱',
                autoOffer: {
                  enabled: true,
                  timeLimit: 24,
                  requiredCourses: [
                    { courseId: '5', courseName: 'Mobile App Development', minimumGrade: 85 },
                    { courseId: '6', courseName: 'React Native Mastery', minimumGrade: 80 }
                  ],
                  minimumOverallScore: 85
                },
                autoOfferDeadline: new Date(Date.now() + 24 * 60 * 60 * 1000)
              },
              {
                id: 6,
                title: 'DevOps Engineer',
                employerName: 'InfraTech',
                location: 'Chicago, IL',
                salaryRange: { min: 95000, max: 140000 },
                applicationsCount: 12,
                skillsRequired: ['Docker', 'Kubernetes', 'CI/CD'],
                requiredCourses: ['DevOps Fundamentals', 'Container Orchestration'],
                logo: '🔧'
              }
            ]).map((job) => {
              // Calculate skill match for display (stable hash-based for consistency)
              const skillMatch = job.skillMatch || (Math.abs(job.id.toString().split('').reduce((a: number, b: string) => (a<<5)-a+b.charCodeAt(0)&0, 0)) % 30) + 70;
              const salary = job.salaryRange ? 
                `$${Math.floor(job.salaryRange.min/1000)}k - $${Math.floor(job.salaryRange.max/1000)}k` :
                job.salary || 'Competitive';
              const company = job.employerName || job.company;
              const applicants = job.applicationsCount || job.applicants;
              const requiredSkills = job.skillsRequired || job.requiredSkills || [];
              
              // Get job-specific qualifying courses based on job title and skills
              const getQualifyingCourses = (jobTitle: string, skills: string[]): string[] => {
                const titleLower = jobTitle.toLowerCase();
                
                // Job-specific course recommendations
                if (titleLower.includes('react') || titleLower.includes('frontend') || skills.some(s => s.toLowerCase().includes('react'))) {
                  return ['Advanced React Development', 'TypeScript Fundamentals'];
                }
                if (titleLower.includes('data') || titleLower.includes('analyst') || skills.some(s => ['python', 'sql', 'tableau'].includes(s.toLowerCase()))) {
                  return ['Data Analysis with Python', 'SQL Mastery'];
                }
                if (titleLower.includes('ux') || titleLower.includes('design') || skills.some(s => s.toLowerCase().includes('figma'))) {
                  return ['UX Design Fundamentals', 'Advanced Prototyping'];
                }
                if (titleLower.includes('backend') || titleLower.includes('server') || skills.some(s => ['node.js', 'aws', 'mongodb'].includes(s.toLowerCase()))) {
                  return ['Backend Development', 'Cloud Architecture'];
                }
                if (titleLower.includes('mobile') || skills.some(s => ['react native', 'ios', 'android'].includes(s.toLowerCase()))) {
                  return ['Mobile App Development', 'React Native Mastery'];
                }
                if (titleLower.includes('devops') || skills.some(s => ['docker', 'kubernetes', 'ci/cd'].includes(s.toLowerCase()))) {
                  return ['DevOps Fundamentals', 'Container Orchestration'];
                }
                if (titleLower.includes('machine learning') || titleLower.includes('ai') || skills.some(s => s.toLowerCase().includes('machine learning'))) {
                  return ['Machine Learning Fundamentals', 'Deep Learning with Python'];
                }
                if (titleLower.includes('security') || titleLower.includes('cyber') || skills.some(s => s.toLowerCase().includes('security'))) {
                  return ['Cybersecurity Fundamentals', 'Ethical Hacking'];
                }
                if (titleLower.includes('product') || titleLower.includes('manager') || skills.some(s => ['strategy', 'analytics'].includes(s.toLowerCase()))) {
                  return ['Product Management Essentials', 'Strategic Analytics'];
                }
                
                // Default fallback
                return ['Professional Skills Development', 'Industry Certification Prep'];
              };
              
              const coursesRequired = job.requiredCourses || job.coursesRequired || getQualifyingCourses(job.title, requiredSkills);
              
              const isAutoOffer = job.autoOffer?.enabled;
              const timeRemaining = isAutoOffer && job.autoOfferDeadline ? (() => {
                const now = new Date();
                let deadline: Date;
                
                // Handle different deadline formats (Firebase timestamp, string, Date)
                if (typeof job.autoOfferDeadline === 'object' && job.autoOfferDeadline && 'seconds' in job.autoOfferDeadline) {
                  // Firebase timestamp with seconds property
                  deadline = new Date((job.autoOfferDeadline as any).seconds * 1000);
                } else if (typeof job.autoOfferDeadline === 'object' && job.autoOfferDeadline && '_seconds' in job.autoOfferDeadline) {
                  // Firebase timestamp with _seconds property
                  deadline = new Date((job.autoOfferDeadline as any)._seconds * 1000);
                } else if (typeof job.autoOfferDeadline === 'object' && job.autoOfferDeadline && 'toDate' in job.autoOfferDeadline) {
                  // Firebase timestamp with toDate method
                  deadline = (job.autoOfferDeadline as any).toDate();
                } else {
                  // String or regular Date
                  deadline = new Date(job.autoOfferDeadline);
                }
                
                // Check if deadline is valid
                if (isNaN(deadline.getTime())) {
                  // Only use demo logic for truly invalid dates
                  const randomHours = Math.floor(Math.random() * 72) + 1;
                  const demoDeadline = new Date(Date.now() + randomHours * 60 * 60 * 1000);
                  const demoTimeDiff = demoDeadline.getTime() - now.getTime();
                  const demoHoursLeft = Math.max(0, Math.floor(demoTimeDiff / (1000 * 60 * 60)));
                  const demoMinutesLeft = Math.max(0, Math.floor((demoTimeDiff % (1000 * 60 * 60)) / (1000 * 60)));
                  
                  if (demoHoursLeft > 24) {
                    const days = Math.floor(demoHoursLeft / 24);
                    return `${days}d ${demoHoursLeft % 24}h left`;
                  } else if (demoHoursLeft > 0) {
                    return `${demoHoursLeft}h ${demoMinutesLeft}m left`;
                  } else {
                    return `${demoMinutesLeft}m left`;
                  }
                }
                
                const timeDiff = deadline.getTime() - now.getTime();
                const hoursLeft = Math.max(0, Math.floor(timeDiff / (1000 * 60 * 60)));
                const minutesLeft = Math.max(0, Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60)));
                
                if (timeDiff <= 0) return 'Ended';
                if (hoursLeft > 24) {
                  const days = Math.floor(hoursLeft / 24);
                  return `${days}d ${hoursLeft % 24}h left`;
                } else if (hoursLeft > 0) {
                  return `${hoursLeft}h ${minutesLeft}m left`;
                } else {
                  return `${minutesLeft}m left`;
                }
              })() : null;
              
              return (
              <div key={job.id} className={`${isAutoOffer ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 shadow-lg hover:shadow-xl ring-1 ring-green-200' : 'bg-white border shadow-md hover:shadow-lg'} rounded-lg transition-all duration-300 p-6 ${isAutoOffer ? 'relative' : ''}`}>
                {/* Auto-offer floating badge */}
                {isAutoOffer && (
                  <div className="absolute -top-3 -right-3 z-10">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg border border-green-400 animate-bounce">
                      ⚡ AUTO-HIRE
                    </div>
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center ${isAutoOffer ? 'bg-green-100 ring-2 ring-green-300' : 'bg-gray-100'}`}>
                      {job.logo ? (
                        <img 
                          src={job.logo} 
                          alt={`${company} logo`} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = `
                              <div class="w-full h-full ${isAutoOffer ? 'bg-green-100 text-green-600' : 'bg-primary-100 text-primary-600'} text-sm font-bold rounded-lg flex items-center justify-center">
                                ${company.charAt(0).toUpperCase()}
                              </div>
                            `;
                          }}
                        />
                      ) : (
                        <div className={`w-full h-full text-sm font-bold rounded-lg flex items-center justify-center ${isAutoOffer ? 'bg-green-100 text-green-600' : 'bg-primary-100 text-primary-600'}`}>
                          {company.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className={`font-semibold ${isAutoOffer ? 'text-green-900' : 'text-gray-900'}`}>{job.title}</h4>
                        {isAutoOffer && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs font-bold border border-yellow-200">
                            FAST TRACK
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${isAutoOffer ? 'text-green-700' : 'text-gray-600'}`}>{company}</p>
                      <p className={`text-xs ${isAutoOffer ? 'text-green-600' : 'text-gray-500'}`}>{job.location}</p>
                      {isAutoOffer && timeRemaining && (
                        <p className="text-xs font-bold text-green-700 flex items-center gap-1 mt-1">
                          ⏰ {timeRemaining}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    skillMatch >= 90 ? 'bg-green-100 text-green-800' :
                    skillMatch >= 80 ? 'bg-blue-100 text-blue-800' :
                    skillMatch >= 70 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {currentUser ? (
                      `${skillMatch}% match`
                    ) : (
                      <RedactedValue bars={2} label="match" cta={null} />
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className={`font-medium ${isAutoOffer ? 'text-green-700' : 'text-green-600'}`}>{salary}</span>
                    <div className="flex items-center gap-2">
                      {isAutoOffer && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold border border-green-200">
                          ⚡ AUTO-HIRE
                        </span>
                      )}
                      <span className={`${isAutoOffer ? 'text-green-600' : 'text-gray-500'}`}>
                        {/* Signed-out visitors never receive the count from the API. */}
                        {currentUser ? (
                          `${applicants} applicants`
                        ) : (
                          <RedactedValue bars={2} label="applicants" cta={null} />
                        )}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-xs text-gray-600 mb-1">Required Skills:</p>
                    <div className="flex flex-wrap gap-1">
                      {requiredSkills.map((skill: string, idx: number) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-xs text-gray-700 rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className={`text-xs mb-1 font-medium ${isAutoOffer ? 'text-green-700' : 'text-gray-600'}`}>
                      {isAutoOffer ? '🎓 Required for Auto-Hire:' : 'Qualifying Courses:'}
                    </p>
                    <div className="space-y-1">
                      {(isAutoOffer ? job.autoOffer.requiredCourses : coursesRequired.slice(0, 2)).map((course: any, idx: number) => (
                        <div key={idx} className={`flex items-center justify-between text-xs p-2 rounded ${isAutoOffer ? 'bg-green-100 border border-green-200' : 'bg-gray-50'}`}>
                          <span className={isAutoOffer ? 'text-green-700 font-medium' : 'text-blue-600'}>
                            • {typeof course === 'string' ? course : course.courseName}
                          </span>
                          {isAutoOffer && typeof course !== 'string' && (
                            <span className="text-green-600 font-bold text-xs">
                              {course.minimumGrade}%+
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    {isAutoOffer && (
                      <p className="text-xs text-green-600 mt-2 font-bold">
                        🎯 Meet requirements = instant hire!
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      if (!currentUser) {
                        navigate('/login');
                      } else {
                        navigate(`/jobs/${job.id}`);
                      }
                    }}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-all duration-200 ${
                      isAutoOffer 
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-lg transform hover:scale-105'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                  >
                    {isAutoOffer ? '🚀 Apply Fast!' : 'Apply Now'}
                  </button>
                  <button
                    onClick={() => {
                      if (!currentUser) {
                        navigate('/login');
                      } else {
                        navigate('/courses');
                      }
                    }}
                    className={`flex-1 px-3 py-2 rounded text-sm font-medium transition-colors ${
                      isAutoOffer 
                        ? 'border-2 border-green-600 text-green-700 hover:bg-green-50 font-bold'
                        : 'border border-primary-600 text-primary-600 hover:bg-primary-50'
                    }`}
                  >
                    {isAutoOffer ? '🎓 Get Certified' : 'Qualify'}
                  </button>
                </div>
              </div>
              );
            })}
            </div>
          )}

          <div className="text-center mt-8">
            <Link
              to="/jobs"
              className="btn-primary px-6 py-3"
            >
              Browse All Jobs
            </Link>
          </div>
        </div>
      </section>

      {/* Rankings Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-extrabold text-secondary-900">
              Top Talent Rankings
            </h3>
            <p className="mt-4 text-lg text-secondary-600">
              See how our top performers are excelling and getting hired
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                id: 1,
                name: 'Sarah Johnson',
                title: 'Full Stack Developer',
                rank: 1,
                coursesCompleted: 128,
                jobsHired: 3,
                rating: 4.9,
                aggregateGrade: 'A+',
                gradePercentage: 98.5,
                qualityScore: 9.8,
                skills: ['React', 'Node.js', 'Python'],
                avatar: '/images/students/sarah.jpg',
                company: 'Google',
                salary: '$140k'
              },
              {
                id: 2,
                name: 'Michael Chen',
                title: 'Data Scientist',
                rank: 2,
                coursesCompleted: 151,
                jobsHired: 2,
                rating: 4.8,
                aggregateGrade: 'A+',
                gradePercentage: 96.2,
                qualityScore: 9.7,
                skills: ['Python', 'Machine Learning', 'SQL'],
                avatar: '/images/students/michael.jpg',
                company: 'Meta',
                salary: '$135k'
              },
              {
                id: 3,
                name: 'Emily Rodriguez',
                title: 'UX Designer',
                rank: 3,
                coursesCompleted: 80,
                jobsHired: 4,
                rating: 4.9,
                aggregateGrade: 'A',
                gradePercentage: 94.7,
                qualityScore: 9.7,
                skills: ['Figma', 'User Research', 'Prototyping'],
                avatar: '/images/students/emily.jpg',
                company: 'Apple',
                salary: '$125k'
              },
              {
                id: 4,
                name: 'David Kim',
                title: 'DevOps Engineer',
                rank: 4,
                coursesCompleted: 100,
                jobsHired: 2,
                rating: 4.7,
                aggregateGrade: 'A',
                gradePercentage: 92.8,
                qualityScore: 9.7,
                skills: ['AWS', 'Docker', 'Kubernetes'],
                avatar: '/images/students/david.jpg',
                company: 'Amazon',
                salary: '$130k'
              },
              {
                id: 5,
                name: 'Lisa Thompson',
                title: 'Product Manager',
                rank: 5,
                coursesCompleted: 119,
                jobsHired: 3,
                rating: 4.8,
                aggregateGrade: 'A-',
                gradePercentage: 91.4,
                qualityScore: 9.7,
                skills: ['Strategy', 'Analytics', 'Leadership'],
                avatar: '/images/students/lisa.jpg',
                company: 'Microsoft',
                salary: '$145k'
              },
              {
                id: 6,
                name: 'James Wilson',
                title: 'Mobile Developer',
                rank: 6,
                coursesCompleted: 111,
                jobsHired: 2,
                rating: 4.6,
                aggregateGrade: 'A-',
                gradePercentage: 89.6,
                qualityScore: 9.6,
                skills: ['React Native', 'iOS', 'Android'],
                avatar: '/images/students/james.jpg',
                company: 'Uber',
                salary: '$120k'
              }
            ].map((candidate) => (
              <div key={candidate.id} className="bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                {/* Header with avatar and rank */}
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 px-6 py-4 flex items-center space-x-4">
                  <div className="relative">
                    <img 
                      src={candidate.avatar} 
                      alt={candidate.name}
                      className="w-16 h-16 rounded-full border-2 border-white shadow-md"
                    />
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      #{candidate.rank}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{candidate.name}</h4>
                    <p className="text-sm text-gray-600">{candidate.title}</p>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center">
                        <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                        <span className="text-sm text-gray-600">{candidate.rating}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          candidate.aggregateGrade.includes('+') ? 'bg-green-100 text-green-800' :
                          candidate.aggregateGrade.includes('-') ? 'bg-blue-100 text-blue-800' :
                          'bg-emerald-100 text-emerald-800'
                        }`}>
                          {candidate.aggregateGrade}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center">
                      <div className="text-xl font-bold text-primary-600">{candidate.coursesCompleted}</div>
                      <div className="text-xs text-gray-500">Courses</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-600">{candidate.jobsHired}</div>
                      <div className="text-xs text-gray-500">Jobs Hired</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-purple-600">{candidate.qualityScore}</div>
                      <div className="text-xs text-gray-500">Quality Score</div>
                    </div>
                  </div>

                  {/* Grade Performance Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-600">Course Performance</p>
                      <span className="text-xs font-medium text-gray-900">{candidate.gradePercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          candidate.gradePercentage >= 95 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                          candidate.gradePercentage >= 90 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                          'bg-gradient-to-r from-yellow-500 to-orange-500'
                        }`}
                        style={{ width: `${candidate.gradePercentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-xs text-gray-600 mb-2">Top Skills:</p>
                    <div className="flex flex-wrap gap-1">
                      {candidate.skills.map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{candidate.company}</p>
                        <p className="text-sm text-green-600 font-medium">{candidate.salary}</p>
                      </div>
                      <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                        View Profile
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

            {/* <div className="text-center mt-8">
            <Link
              to="/rankings"
              className="btn-primary px-6 py-3"
            >
              View Full Rankings
            </Link>
          </div> */}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 text-center">
            <div>
              <div className="text-4xl font-bold text-white">100,000+</div>
              <div className="mt-2 text-primary-100">Students Enrolled</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white">500+</div>
              <div className="mt-2 text-primary-100">Partner Companies</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white">1,000+</div>
              <div className="mt-2 text-primary-100">Courses Available</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-white">95%</div>
              <div className="mt-2 text-primary-100">Job Placement Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-3xl font-extrabold text-secondary-900">
              How Workazon Works
            </h3>
            <p className="mt-4 text-lg text-secondary-600">
              Simple steps to transform your career
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-primary-600">1</span>
              </div>
              <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                Learn & Practice
              </h4>
              <p className="mt-2 text-secondary-600">
                Take courses, complete quizzes, and build your skills with our comprehensive curriculum.
              </p>
            </div>

            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-primary-600">2</span>
              </div>
              <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                Get Certified
              </h4>
              <p className="mt-2 text-secondary-600">
                Earn AI-validated certifications that prove your competency to employers.
              </p>
            </div>

            <div className="text-center">
              <div className="mx-auto h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-primary-600">3</span>
              </div>
              <h4 className="mt-4 text-xl font-semibold text-secondary-900">
                Get Hired
              </h4>
              <p className="mt-2 text-secondary-600">
                Connect with employers and get matched to jobs that fit your skills perfectly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-extrabold text-secondary-900">
              Level Up Your Skills
            </h3>
            <p className="mt-4 text-lg text-secondary-600">
              Choose from our industry-leading courses and get certified
            </p>
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {['All', 'Technology', 'Finance', 'Healthcare', 'Design', 'Marketing', 'Data Science'].map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCourseCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                  category === selectedCourseCategory 
                    ? 'bg-primary-600 text-white shadow-md transform scale-105' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                }`}
              >
                <span>
                  {category}
                </span>
              </button>
            ))}
          </div>

          {courses.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Sign in for more courses</p> 
              <p className="text-sm text-gray-400 mt-2">Access our full course catalog by logging in!</p>
            </div>
          ) : (
            (() => {
              // Filter courses based on selected category using helper function
              const filteredCourses = courses.filter(course => 
                matchesCourseCategory(course, selectedCourseCategory)
              );

              // Limit to 6 courses ONLY when "All" is selected, show all for specific categories
              const displayedCourses = selectedCourseCategory === 'All' 
                ? filteredCourses.slice(0, 6) 
                : filteredCourses;

              // Show "sign in" message if filtering returns empty
              if (displayedCourses.length === 0) {
                return (
                  <div className="text-center py-12">
                    <div className="mx-auto h-16 w-16 text-gray-400 mb-4">
                      <BookOpenIcon className="h-full w-full" />
                    </div>
                    <p className="text-gray-500 text-lg font-medium">
                      Sign in for more courses
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      Access our full course catalog by logging in!
                    </p>
                    <button
                      onClick={() => setSelectedCourseCategory('All')}
                      className="mt-4 text-primary-600 hover:text-primary-700 font-medium text-sm underline"
                    >
                      View All Courses
                    </button>
                  </div>
                );
              }

              // Render filtered courses
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {displayedCourses.map((course) => {
              const instructor = course.instructorName || course.instructor;
              const duration = course.estimatedWeeks ? `${course.estimatedWeeks} weeks` : course.duration;
              const price = course.price === 0 ? 'Free' : `$${course.price}`;
              const rating = course.averageRating || course.rating;
              const students = course.enrollmentCount || course.students || 0;
              const skills = course.skills || [];
              const image = course.thumbnailUrl || course.image;
              
              return (
              <div key={course.id} className="bg-white border border-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                      <img src={image} alt={course.title} className="w-full h-full object-cover" />
                    </div>
                    <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded">
                      {course.industryCategory || course.category}
                    </span>
                  </div>
                  
                  <h4 className="font-semibold text-gray-900 mb-2">{course.title}</h4>
                  <p className="text-sm text-gray-600 mb-3">by {instructor}</p>
                  
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {duration}
                    </span>
                    <span className="flex items-center">
                      <StarIcon className="h-4 w-4 mr-1 text-yellow-400" />
                      {rating}
                    </span>
                    <span className="flex items-center">
                      <UsersIcon className="h-4 w-4 mr-1" />
                      {students.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-xs text-gray-600 mb-2">Skills you'll learn:</p>
                    <div className="flex flex-wrap gap-1">
                      {skills.map((skill: string, idx: number) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-green-600 flex items-center">
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                        {price}
                      </span>
                    </span>
                    <button
                      onClick={() => {
                        if (!currentUser) {
                          navigate('/login');
                        } else {
                          navigate(`/courses/${course.id}`);
                        }
                      }}
                      className="bg-primary-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-primary-700 transition-colors"
                    >
                      Enroll Now
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
                </div>
              );
            })()
          )}

          <div className="text-center mt-8">
            <Link
              to="/courses"
              className="btn-primary px-6 py-3"
            >
              Browse All Courses
            </Link>
          </div>
        </div>
      </section>

      {/* Top Employers Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-extrabold text-secondary-900">
              Top Employers Hiring Now
            </h3>
            <p className="mt-4 text-lg text-secondary-600">
              Connect with leading companies actively seeking talent
            </p>
          </div>

          {topEmployers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No employers found at the moment.</p>
              <p className="text-sm text-gray-400 mt-2">Check back later for new opportunities!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {topEmployers.map((employer) => {
              // Format employer data for consistent display
              const employerJobs = employer.recentJobs || employer.jobs || [];
              const formattedJobs = employerJobs.slice(0, 5).map((job: any) => ({
                id: job.id,
                title: stripHtml(job.title || ''),
                salary: job.salaryRange ? 
                  `$${Math.floor(job.salaryRange.min/1000)}k - $${Math.floor(job.salaryRange.max/1000)}k` :
                  job.salary || 'Competitive',
                location: stripHtml(job.location || 'Remote'),
                applicants: job.applicationsCount || job.applicants || 0
              }));
              
              return (
              <div key={employer.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                      {employer.logo ? (
                        <img 
                          src={employer.logo} 
                          alt={`${employer.companyName || employer.name} logo`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const companyName = employer.companyName || employer.name;
                            target.parentElement!.innerHTML = `
                              <div class="w-full h-full bg-primary-100 text-primary-600 text-xl font-bold rounded-lg flex items-center justify-center">
                                ${companyName.charAt(0)}
                              </div>
                            `;
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-primary-100 text-primary-600 text-xl font-bold rounded-lg flex items-center justify-center">
                          {(employer.companyName || employer.name).charAt(0)}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{employer.companyName || employer.name}</h4>
                      <p className="text-sm text-gray-600">{employer.companyDescription || employer.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>{employer.employeeCount || employer.employees} employees</span>
                    <span>•</span>
                    <span>{employer.industry}</span>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-gray-900">Open Positions</h5>
                    <span className="text-sm text-gray-500">{employer.activeJobs || formattedJobs.length} jobs</span>
                  </div>
                  
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {formattedJobs.map((job: any, idx: number) => (
                      <div key={idx} className="border border-gray-100 rounded p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                           onClick={() => navigate(`/jobs/${job.id}`)}>
                        <div className="flex justify-between items-start mb-2">
                          <h6 className="font-medium text-sm text-gray-900 flex-1 pr-2 leading-tight break-words overflow-hidden"
                              style={{ 
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                wordBreak: 'break-word'
                              }}>
                            {job.title}
                          </h6>
                          <span className="text-xs text-green-600 font-medium flex-shrink-0">{job.salary}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>{job.location}</span>
                          <span>{job.applicants} applicants</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => navigate('/jobs')}
                    className="w-full mt-4 text-primary-600 hover:text-primary-700 text-sm font-medium text-center border border-primary-600 hover:bg-primary-50 py-2 rounded transition-colors"
                  >
                    View All Jobs at {employer.companyName || employer.name}
                  </button>
                </div>
              </div>
              );
              })}
            </div>
          )}

          {topEmployers.length > 0 && (
            <div className="text-center mt-12">
              <Link
                to="/jobs"
                className="btn-primary px-6 py-3"
              >
                Explore All Employers
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-extrabold text-secondary-900">
            Ready to Bridge Your Skills to Success?
          </h3>
          <p className="mt-4 text-xl text-secondary-600">
            Join thousands of students and employers already using Workazon
          </p>
          <div className="mt-8 flex justify-center space-x-4">
            <Link
              to="/register?type=student"
              className="btn-primary px-8 py-3 text-lg"
            >
              Start as Student
            </Link>
            <Link
              to="/register?type=employer"
              className="btn-secondary px-8 py-3 text-lg"
            >
              Join as Employer
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary-800">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h4 className="text-2xl font-bold text-white">Workazon</h4>
              <p className="mt-4 text-secondary-300">
                Connecting skills with opportunities. The future of education and employment.
              </p>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-white">For Students</h5>
              <ul className="mt-4 space-y-2">
                <li><a href="#" className="text-secondary-300 hover:text-white">Courses</a></li>
                <li><a href="#" className="text-secondary-300 hover:text-white">Certifications</a></li>
                <li><a href="#" className="text-secondary-300 hover:text-white">Job Board</a></li>
                <li><a href="/contact" className="text-secondary-300 hover:text-white">Contact Us</a></li>
                  {/* <li><a href="#" className="text-secondary-300 hover:text-white">Rankings</a></li>   */} 
              </ul>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-white">For Employers</h5>
              <ul className="mt-4 space-y-2">
                <li><a href="#" className="text-secondary-300 hover:text-white">Post Jobs</a></li>
                <li><a href="#" className="text-secondary-300 hover:text-white">Find Talent</a></li>
                <li><a href="#" className="text-secondary-300 hover:text-white">Custom Courses</a></li>
                <li><a href="#" className="text-secondary-300 hover:text-white">Pricing</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-secondary-700">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-secondary-300 mb-4 md:mb-0">
                © {new Date().getFullYear()} Workazon. All rights reserved.
              </p>
              <div className="flex space-x-6">
                <Link 
                  to="/terms" 
                  className="text-secondary-300 hover:text-white transition-colors text-sm"
                >
                  Terms & Conditions
                </Link>
                <Link 
                  to="/privacy" 
                  className="text-secondary-300 hover:text-white transition-colors text-sm"
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 